/**
 * "Link Sui" (Accounts v2, act-babylon DR-0053): the exact message a player's
 * Sui wallet signs to add its address to an ACT account. The server rebuilds
 * nothing: it parses this text, checks the account and the address in it
 * against the request, checks the time, then checks the signature. Naming the
 * account inside the signed text is what makes a replayed signature harmless:
 * it can only ever link that address to that account.
 */

/** How old a signed link message may be when the server checks it. */
export const SUI_LINK_MAX_AGE_MS = 10 * 60_000;

const LINK_HEADER = "ACT: link this Sui address to my ACT account.";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** A Sui address in canonical form (0x + 64 lowercase hex), or null. Short forms are zero-padded. */
export function normalizeSuiAddress(value: string): string | null {
  const m = /^0x([0-9a-fA-F]{1,64})$/.exec(value.trim());
  return m ? `0x${m[1]!.toLowerCase().padStart(64, "0")}` : null;
}

/** "0x1a2b…9f0e" for display. */
export function shortSuiAddress(address: string): string {
  return address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}

export type SuiLinkClaim = { address: string; accountUuid: string; issuedAt: Date };

/** The text a wallet signs. Players see it in their wallet, so it stays plain. */
export function suiLinkMessage(claim: SuiLinkClaim): string {
  return [
    LINK_HEADER,
    "",
    `Address: ${claim.address}`,
    `Account: ${claim.accountUuid}`,
    `Issued: ${claim.issuedAt.toISOString()}`,
  ].join("\n");
}

/** The claim inside a message made by {@link suiLinkMessage}, or null if it isn't one exactly. */
export function parseSuiLinkMessage(message: string): SuiLinkClaim | null {
  const lines = message.split("\n");
  if (lines.length !== 5 || lines[0] !== LINK_HEADER || lines[1] !== "") return null;
  const address = /^Address: (0x[0-9a-f]{64})$/.exec(lines[2]!)?.[1];
  const accountUuid = /^Account: (.+)$/.exec(lines[3]!)?.[1];
  const issued = /^Issued: (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)$/.exec(lines[4]!)?.[1];
  if (!address || !accountUuid || !UUID.test(accountUuid) || !issued) return null;
  const issuedAt = new Date(issued);
  if (Number.isNaN(issuedAt.getTime())) return null;
  return { address, accountUuid, issuedAt };
}

/** True when a claim was issued within {@link SUI_LINK_MAX_AGE_MS} of `now` (either side, for clock skew). */
export function suiLinkClaimFresh(claim: SuiLinkClaim, now: Date): boolean {
  return Math.abs(now.getTime() - claim.issuedAt.getTime()) <= SUI_LINK_MAX_AGE_MS;
}
